/**
 * Shared abstract base for the codebase's two structured-error families:
 * the expression engine's `ExprError` (`#core/lib/expr/errors.ts`) and the
 * chat plugin's `ToolExecutionError` (`#plugin-chat/lib/tool/errors/base.ts`).
 *
 * Both independently reimplemented the same implicit protocol — prototype-chain
 * restoration + a synced `this.name`, a reflective `details()` that excludes a
 * small set of reserved fields, a `suggestions()` recovery default, and a
 * `format()` rendering contract. That scaffolding lives here once; per-domain
 * content (source positions, recovery strategies, wire serialisation, …) stays
 * in the subclasses.
 */

/** Reserved keys every structured error hides from {@link StructuredError.details}. */
const BASE_RESERVED_DETAIL_KEYS: readonly string[] = ['message', 'stack', 'cause'];

/**
 * Abstract base implementing the structured-error protocol shared by the
 * expression engine and the chat tool layer.
 *
 * Subclasses are concrete: instantiate them directly, throw, and catch via
 * `instanceof` (the constructor restores the prototype chain so this holds
 * across module boundaries / after transpilation).
 */
export abstract class StructuredError extends Error {
  constructor(message: string) {
    super(message);
    // Restore the prototype chain across module boundaries — subclass
    // `instanceof` checks would otherwise break after TS-compiled output
    // subclasses the built-in `Error`.
    Object.setPrototypeOf(this, new.target.prototype);
    // Mirror the resolved class name onto the standard `Error.name` field,
    // which every JS-native consumer (DevTools, `err.toString()`, vitest's
    // `.rejects.toThrowError`) reads.
    this.name = this.resolveName(new.target);
  }

  /**
   * Resolve the value assigned to `this.name`. Defaults to the runtime class
   * name. Override when class names are not reliable at runtime (e.g. the
   * chat layer reads a minification-safe static discriminator instead).
   */
  protected resolveName(target: abstract new (...args: never[]) => StructuredError): string {
    return target.name;
  }

  /**
   * Own enumerable keys excluded from the default {@link details} passthrough.
   * The base hides the reserved `Error` fields; subclasses extend this with
   * their own non-structural own fields (internal hints, sibling-surfaced
   * recovery metadata, …).
   */
  protected reservedDetailKeys(): readonly string[] {
    return BASE_RESERVED_DETAIL_KEYS;
  }

  /** Recovery suggestions for callers / LLMs. Override per-class. Default empty. */
  suggestions(): readonly string[] {
    return [];
  }

  /**
   * Structured view of the error for logging or wire serialisation. Default
   * impl reflects own enumerable properties minus {@link reservedDetailKeys}.
   * Override when a custom shape is needed (computed fields, renames, …).
   */
  details(): Readonly<Record<string, unknown>> {
    const reserved = new Set(this.reservedDetailKeys());
    return Object.fromEntries(Object.entries(this).filter(([key]) => !reserved.has(key)));
  }

  /** Render this error as text for logs / the user / the LLM. */
  abstract format(): string;
}
